import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { workerEventService } from "../services/workerEventService";
import { workerEventDisplayId } from "./WorkerEventCommonStore";
import { WorkerEvent } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class WorkerEventViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = workerEventService;
  workerEventInstance: WorkerEvent | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  queueNameValue: string = "";
  jobTypeValue: string = "";
  statusValue: string = "";
  enqueuedAtValue: Date | null = null;
  startedAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  payloadValue: string = "{}";
  resultValue: string = "{}";
  errorValue: string = "{}";
  retryCountValue: number = 0;
  manualRetryCountValue: number = 0;
  nextRetryAtValue: Date | null = null;
  durationValue: number | undefined = undefined;
  correlationIdValue: string = "";
  triggerUserIdService = userService;
  triggerUserIdSearchValue: string = "";
  triggerUserIdIdValue: string = "";
  triggerUserIdSelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadWorkerEventById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.workerEventOne({ id });
    this.workerEventInstance = result.workerEventOne;
    if (this.workerEventInstance) {
      this.loadValues(this.workerEventInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.workerEventInstance) {
      return workerEventDisplayId(this.workerEventInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkerEventSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "triggerUserId") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.triggerUserIdSelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get triggerUserIdValue() {
    if (this.triggerUserIdSelectedValue) {
      return {
        id: this.triggerUserIdSelectedValue.id,
        displayId: userDisplayId(
          this.triggerUserIdSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.workerEventInstance = null;
    this.queueNameValue = "";
    this.jobTypeValue = "";
    this.statusValue = "";
    this.enqueuedAtValue = null;
    this.startedAtValue = null;
    this.completedAtValue = null;
    this.payloadValue = "{}";
    this.resultValue = "{}";
    this.errorValue = "{}";
    this.retryCountValue = 0;
    this.manualRetryCountValue = 0;
    this.nextRetryAtValue = null;
    this.durationValue = undefined;
    this.correlationIdValue = "";
    this.triggerUserIdSearchValue = "";
    this.triggerUserIdIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelWorkerEvent() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editWorkerEvent() {
    const id = this.workerEventInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/workerEvent/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printWorkerEvent(handlePrint: () => void): void {
    handlePrint();
  }

  *closeWorkerEvent(): any {
    yield this.close();
  }

  loadValues(instance: WorkerEvent) {
    this.idValue = instance.id;
    this.queueNameValue = instance.queueName;
    this.jobTypeValue = instance.jobType;
    this.statusValue = instance.status;
    this.enqueuedAtValue = new Date(instance.enqueuedAt);
    this.startedAtValue = instance.startedAt
      ? new Date(instance.startedAt)
      : null;
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.payloadValue = instance.payload;
    this.resultValue = instance.result || "";
    this.errorValue = instance.error || "";
    this.retryCountValue = instance.retryCount;
    this.manualRetryCountValue = instance.manualRetryCount;
    this.nextRetryAtValue = instance.nextRetryAt
      ? new Date(instance.nextRetryAt)
      : null;
    this.durationValue = instance.duration || undefined;
    this.correlationIdValue = instance.correlationId || "";
    this.triggerUserIdSelectedValue = instance.triggerUserId;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadWorkerEventById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
